import { useState } from 'react'
import { Form, Button, message } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/useAuthContext'
import { useTranslation } from 'react-i18next'
import { BerlixInput, BerlixPassword } from '../../components/common/BerlixInput'
import './LoginPage.css'
import './LoginPage-responsive.css'

const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn } = useAuth()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  const from = location.state?.from?.pathname || '/admin'

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true)
    try {
      await signIn(values.email, values.password)
      message.success(t('auth.loginSuccess', { defaultValue: 'Login successful' }))
      navigate(from, { replace: true })
    } catch (error) {
      message.error(t('auth.loginError', { defaultValue: 'Invalid email or password' }))
      console.error('Login error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-brand">
          <h1>{t('brand.name', { defaultValue: 'WhistlePro' })}</h1>
          <p>{t('auth.adminPortal', { defaultValue: 'Admin Portal' })}</p>
        </div>

        <Form
          form={form}
          name="login"
          className="login-form"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('validation.emailRequired', { defaultValue: 'Please enter your email' }) },
              { type: 'email', message: t('validation.emailInvalid', { defaultValue: 'Please enter a valid email' }) }
            ]}
          >
            <BerlixInput
              icon={<UserOutlined />}
              placeholder={t('auth.emailPlaceholder', { defaultValue: 'Email address' })}
              autoComplete="email"
              disabled={loading}
            />
          </Form.Item>
          
          <Form.Item
            name="password"
            rules={[{ required: true, message: t('validation.passwordRequired', { defaultValue: 'Please enter your password' }) }]}
          >
            <BerlixPassword
              icon={<LockOutlined />}
              placeholder={t('auth.passwordPlaceholder', { defaultValue: 'Password' })}
              autoComplete="current-password"
              disabled={loading}
            />
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              size="large"
            >
              {t('auth.signIn', { defaultValue: 'Sign In' })}
            </Button>
          </Form.Item>
        </Form>

        <div className="login-links">
          <a href="/">{t('auth.backToHome', { defaultValue: 'Back to Home' })}</a>
          <span className="separator">•</span>
          <a href="#">{t('auth.needHelp', { defaultValue: 'Need Help?' })}</a>
        </div>
      </div>
    </div>
  )
}

export default LoginPage